import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@produzionejb/ui';
import { usePackagingItems } from '../../../lib/packaging-items-api';

type CategoryStat = {
  category: string;
  count: number;
  totalQty: number;
};

export function PackagingStockWidget() {
  const { t } = useTranslation();
  const itemsQ = usePackagingItems({ active: true, pageSize: 500 });

  const stats = useMemo<CategoryStat[]>(() => {
    const items = itemsQ.data?.items ?? [];
    const map = new Map<string, CategoryStat>();
    for (const it of items) {
      const cur = map.get(it.category) ?? { category: it.category, count: 0, totalQty: 0 };
      cur.count++;
      cur.totalQty += it.residualQty;
      map.set(it.category, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.totalQty - a.totalQty);
  }, [itemsQ.data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('dashboard.packagingStock.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {itemsQ.isLoading && <p className="text-sm text-slate-500">{t('common.loading')}</p>}
        {!itemsQ.isLoading && stats.length === 0 && (
          <p className="text-sm text-slate-500">{t('dashboard.packagingStock.empty')}</p>
        )}
        {stats.length > 0 && (
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-2 py-1 text-left font-medium text-slate-600">
                    {t('dashboard.packagingStock.col.category')}
                  </th>
                  <th className="px-2 py-1 text-right font-medium text-slate-600">
                    {t('dashboard.packagingStock.col.itemCount')}
                  </th>
                  <th className="px-2 py-1 text-right font-medium text-slate-600">
                    {t('dashboard.packagingStock.col.totalQty')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.map((s) => (
                  <tr key={s.category} className="border-b border-slate-100 last:border-0">
                    <td className="px-2 py-1 text-slate-700">{s.category}</td>
                    <td className="px-2 py-1 text-right font-mono">{s.count}</td>
                    <td className="px-2 py-1 text-right font-mono">{s.totalQty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
